import * as React from "react"
import DOMPurify from "dompurify"
import AddStepButton from "./AddStepButton"
import { ArrowRightIcon, GripIcon, TrashIcon } from "./icons"
import { WidgetTourStep, WidgetTourStepType } from "../../types"

interface IProps {
  steps: WidgetTourStep[]
  onAddClick: (type: WidgetTourStepType) => void
  onStepClick: (step: WidgetTourStep) => void
  onRemoveClick: (step: WidgetTourStep) => void
  onReorder: (orderedIds: string[]) => void
}

// @hello-pangea/dnd (like its react-beautiful-dnd upstream) locates draggables
// via `document.querySelectorAll`, which can never see into the shadow root
// this whole tour builder renders in - drags silently no-op. Reordering here
// is a small hand-rolled pointer-drag instead, since it only ever touches
// elements we already hold refs to.
const StepStrip = ({ steps, onAddClick, onStepClick, onRemoveClick, onReorder }: IProps) => {
  const [order, setOrder] = React.useState(() => steps.map((step) => step.id!))
  const [draggingId, setDraggingId] = React.useState<string | null>(null)
  const cardRefs = React.useRef(new Map<string, HTMLDivElement>())
  const stepsById = React.useMemo(() => new Map(steps.map((step) => [step.id!, step])), [steps])

  React.useEffect(() => {
    const incomingIds = steps.map((step) => step.id!)
    setOrder((current) => {
      const isSame = current.length === incomingIds.length && current.every((id, i) => id === incomingIds[i])
      return isSame ? current : incomingIds
    })
  }, [steps])

  const setCardRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) cardRefs.current.set(id, el)
    else cardRefs.current.delete(id)
  }

  const onHandlePointerDown = (id: string) => (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    setDraggingId(id)
  }

  const onHandlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!draggingId) return
    const pointerX = e.clientX

    setOrder((current) => {
      const dragIndex = current.indexOf(draggingId)
      if (dragIndex === -1) return current

      let closestIndex = dragIndex
      let closestDistance = Infinity
      current.forEach((id, index) => {
        const el = cardRefs.current.get(id)
        if (!el) return
        const rect = el.getBoundingClientRect()
        const distance = Math.abs(pointerX - (rect.left + rect.width / 2))
        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      })

      if (closestIndex === dragIndex) return current

      const next = [...current]
      next.splice(dragIndex, 1)
      next.splice(closestIndex, 0, draggingId)
      return next
    })
  }

  const onHandlePointerUp = () => {
    if (!draggingId) return
    setDraggingId(null)
    onReorder(order)
  }

  return (
    <div className="bizzey-tb-step-strip">
      {order.map((id, index) => {
        const step = stepsById.get(id)
        if (!step) return null

        return (
          <React.Fragment key={id}>
            <div
              className={`bizzey-tb-step-card${draggingId === id ? " bizzey-tb-step-card--dragging" : ""}`}
              ref={setCardRef(id)}
              onClick={() => onStepClick(step)}
            >
              <div className="bizzey-tb-step-card__text">
                <div className="bizzey-tb-step-card__title">{step.title || "Untitled step"}</div>
                {step.description && (
                  <div
                    className="bizzey-tb-step-card__description"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step.description) }}
                  />
                )}
              </div>

              <div className="bizzey-tb-step-card__controls">
                <button
                  type="button"
                  className="bizzey-tb-step-card__handle"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={onHandlePointerDown(id)}
                  onPointerMove={onHandlePointerMove}
                  onPointerUp={onHandlePointerUp}
                  aria-label="Reorder step"
                >
                  <GripIcon />
                </button>
                <button
                  type="button"
                  className="bizzey-tb-step-card__delete"
                  onClick={(e) => { e.stopPropagation(); onRemoveClick(step) }}
                  aria-label="Remove step"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>

            {index < order.length - 1 && (
              <span className="bizzey-tb-step-connector">
                <ArrowRightIcon />
              </span>
            )}
          </React.Fragment>
        )
      })}

      {order.length > 0 && (
        <span className="bizzey-tb-step-connector">
          <ArrowRightIcon />
        </span>
      )}

      <AddStepButton onAddClick={onAddClick} />
    </div>
  )
}

export default StepStrip
